/**
 * Legal pages: gives pasted policy text a real heading structure and builds an
 * "On this page" index with the current section highlighted while scrolling.
 *
 * Works on <eme-legal> (pages using the legal template) and on Shopify's own
 * /policies/* pages, which we can't template, by enhancing their markup in place.
 */

const MOBILE = window.matchMedia('(max-width: 989px)');

/** Policies pasted from docs often use bold paragraphs instead of headings */
function promoteBoldParagraphs(body) {
  if (body.querySelector('h2, h3')) return;

  for (const p of body.querySelectorAll('p')) {
    const text = p.textContent.trim();
    const onlyChild = p.children.length === 1 && p.firstElementChild.matches('strong, b');
    if (!onlyChild || text !== p.firstElementChild.textContent.trim() || text.length > 90) continue;

    const h2 = document.createElement('h2');
    h2.textContent = text;
    p.replaceWith(h2);
  }
}

function slugify(text, used) {
  const base =
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';
  let slug = base;
  for (let i = 2; used.has(slug); i++) slug = `${base}-${i}`;
  used.add(slug);
  return slug;
}

/** Fills `list` with links to the h2s in `body`; returns false when there are too few to bother */
function buildIndex(body, list) {
  promoteBoldParagraphs(body);

  const headings = [...body.querySelectorAll('h2')].filter((h) => h.textContent.trim());
  if (headings.length < 3) return false;

  const used = new Set();
  const links = new Map();

  for (const heading of headings) {
    if (!heading.id) heading.id = slugify(heading.textContent, used);

    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent.trim();
    item.append(link);
    list.append(item);
    links.set(heading, link);
  }

  const setCurrent = (heading) => {
    for (const link of links.values()) link.removeAttribute('aria-current');
    links.get(heading)?.setAttribute('aria-current', 'true');
  };

  // The current section is the last heading that has scrolled past the upper third of the screen
  const observer = new IntersectionObserver(
    () => {
      const current = headings.findLast((h) => h.getBoundingClientRect().top < window.innerHeight / 3);
      setCurrent(current ?? headings[0]);
    },
    { rootMargin: '0px 0px -66% 0px' }
  );
  headings.forEach((h) => observer.observe(h));

  // On small screens the index is a collapsible panel; close it after jumping
  list.addEventListener('click', (event) => {
    if (!event.target.closest('a')) return;
    const details = list.closest('details');
    if (details && MOBILE.matches) details.open = false;
  });

  return true;
}

function syncDetails(details) {
  const update = () => (details.open = !MOBILE.matches);
  update();
  MOBILE.addEventListener('change', update);
}

class EmeLegal extends HTMLElement {
  connectedCallback() {
    const body = this.querySelector('[data-legal-body]');
    const toc = this.querySelector('[data-legal-toc]');
    if (!body || !toc) return;

    if (buildIndex(body, toc.querySelector('ol'))) {
      toc.hidden = false;
      syncDetails(toc);
      return;
    }

    // No index and nothing else in the sidebar: let the article take the full width
    const aside = toc.closest('.eme-legal__aside');
    if (aside && aside.children.length === 1) {
      aside.closest('.eme-legal__layout')?.classList.add('eme-legal__layout--no-aside');
      aside.remove();
    }
  }
}

if (!customElements.get('eme-legal')) customElements.define('eme-legal', EmeLegal);

/** Shopify policy pages: /policies/privacy-policy etc. */
function enhancePolicyPage() {
  const container = document.querySelector('.shopify-policy__container');
  const body = container?.querySelector('.shopify-policy__body');
  const template = document.getElementById('EmeLegalPolicyToc');
  if (!body || !template || container.dataset.enhanced) return;
  container.dataset.enhanced = 'true';

  const toc = template.content.firstElementChild.cloneNode(true);
  if (buildIndex(body, toc.querySelector('ol'))) {
    container.classList.add('eme-legal--has-toc');
    container.insertBefore(toc, body);
    syncDetails(toc);
  }

  const footer = document.getElementById('EmeLegalPolicyFooter');
  if (footer) container.append(footer.content.cloneNode(true));
}

enhancePolicyPage();
